import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Container,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Search as SearchIcon,
  Update as UpdateIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

const StageUpdate = () => {
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Search State
  const [searchInput, setSearchInput] = useState('');
  const [applicationData, setApplicationData] = useState(null);
  const [showApplicationDetails, setShowApplicationDetails] = useState(false);

  // Stage Update State
  const [stages, setStages] = useState([]);
  const [stagesLoading, setStagesLoading] = useState(true);
  const [stagesError, setStagesError] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [stageUpdateLoading, setStageUpdateLoading] = useState(false);

  // Load available stages on component mount
  useEffect(() => {
    loadStages();
  }, []);

  const loadStages = async () => {
    try {
      setStagesLoading(true);
      setStagesError('');
      console.log('Loading stages...'); // Debug log
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/super-admin-master/stages`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        }
      });

      console.log('Stages response status:', response.status); // Debug log
      
      if (response.ok) {
        const data = await response.json();
        console.log('Stages loaded:', data); // Debug log
        if (data.success && data.stages) {
          setStages(data.stages);
        } else {
          setStagesError('No stages found in response');
        }
      } else {
        console.error('Failed to load stages:', response.status, response.statusText);
        const errorData = await response.text();
        console.error('Error response:', errorData);
        setStagesError(`Failed to load stages: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error loading stages:', error);
      setStagesError(`Network error: ${error.message}`);
    } finally {
      setStagesLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchInput.trim()) {
      setError('Please enter a username or application ID');
      return;
    }

    setSearchLoading(true);
    setError('');
    setSuccess('');
    setApplicationData(null);
    setShowApplicationDetails(false);
    setSelectedStage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/super-admin-master/application-stage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          searchInput: searchInput.trim()
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setApplicationData(data.application);
        setSelectedStage(data.application.currentStage?.toString() || '');
        setShowApplicationDetails(true);
      } else {
        setError(data.message || 'Application not found');
        setShowApplicationDetails(false);
      }
    } catch (error) {
      console.error('Search error:', error);
      setError('Network error occurred while searching');
      setShowApplicationDetails(false);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleStageUpdate = async () => {
    if (!selectedStage) {
      setError('Please select a stage to update');
      return;
    }

    if (!applicationData) {
      setError('No application selected');
      return;
    }

    setStageUpdateLoading(true);
    setError('');
    setSuccess('');

    try {
        const stageId = selectedStage
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/super-admin-master/update-stage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          appId: applicationData.appId,
          orgId: applicationData.orgId,
          newStage: stageId,
          applicationNo: applicationData.applicationNo,
          userName: applicationData.userName
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const selectedStageInfo = stages.find(s => {
          const stageId = s.id || s.stageId || s.Id;
          return stageId == selectedStage;
        });
        const selectedStageName = selectedStageInfo?.name || selectedStageInfo?.stageName || selectedStageInfo?.description || 'Unknown Stage';
        
        setSuccess(`Stage updated successfully from "${applicationData.currentStageName}" to "${selectedStageName}"`);
        
        // Refresh application data to show updated stage
        setTimeout(() => {
          handleSearch();
        }, 1000);
      } else {
        setError(data.message || 'Failed to update stage');
      }
    } catch (error) {
      console.error('Stage update error:', error);
      setError('Network error occurred while updating stage');
    } finally {
      setStageUpdateLoading(false);
    }
  };

  const getStageColor = (stageId) => {
    const stageNum = parseInt(stageId);
    if (stageNum <= 5) return 'primary';
    if (stageNum <= 10) return 'warning';
    if (stageNum <= 15) return 'secondary';
    return 'success';
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Application Stage Update
        </Typography>
        
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Search by username (e.g., nabln000668) or application ID (e.g., M-6034) to view and update application stage
        </Typography>
        
        <Divider sx={{ mb: 3 }} />


        {/* Search Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Search Application
            </Typography>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Username or Application ID"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="e.g., nabln000668 or M-6034"
                  helperText="Enter username (nabln000668) or application number (M-6034)"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  disabled={searchLoading}
                  startIcon={searchLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ height: 56 }}
                >
                  {searchLoading ? 'Searching...' : 'Search'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Application Details */}
        {showApplicationDetails && applicationData && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InfoIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Application Details
                </Typography>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell><strong>Application No:</strong></TableCell>
                      <TableCell>{applicationData.applicationNo}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Username:</strong></TableCell>
                      <TableCell>{applicationData.userName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Laboratory Name:</strong></TableCell>
                      <TableCell>{applicationData.laboratoryName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Contact Person:</strong></TableCell>
                      <TableCell>{applicationData.contactPerson}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Lab Type:</strong></TableCell>
                      <TableCell>{applicationData.labTypeName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Assessment Type:</strong></TableCell>
                      <TableCell>{applicationData.assessmentTypeName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Current Stage:</strong></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={`Stage ${applicationData.currentStage}`}
                            color={getStageColor(applicationData.currentStage)}
                            size="small"
                          />
                          <Typography variant="body2">
                            {applicationData.currentStageName}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Application Date:</strong></TableCell>
                      <TableCell>{applicationData.applicationDate}</TableCell>
                    </TableRow>
                    {applicationData.lastUpdated && (
                      <TableRow>
                        <TableCell><strong>Last Updated:</strong></TableCell>
                        <TableCell>{applicationData.lastUpdated}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Stage Update Section */}
        {showApplicationDetails && applicationData && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <UpdateIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Update Application Stage
                </Typography>
              </Box>

              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Select New Stage</InputLabel>
                    <Select
                      value={selectedStage}
                    //   onChange={(e) => setSelectedStage(e.target.value)}
                      onChange={e => {
                        console.log('Selected stage change:', e.target.value);
                        setSelectedStage(e.target.value);
                      }}
                      label="Select New Stage"
                      disabled={stagesLoading}
                    >
                      {stagesLoading ? (
                        <MenuItem disabled>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            <Typography>Loading stages...</Typography>
                          </Box>
                        </MenuItem>
                      ) : stagesError ? (
                        <MenuItem disabled>
                          <Typography color="error">Error: {stagesError}</Typography>
                        </MenuItem>
                      ) : stages.length > 0 ? stages.map((stage) => {
                        // Handle different possible field names from backend
                        const stageId = stage.stage || stage.stageId || stage.Id;
                        const stageName = stage.name || stage.stageName || stage.StageName || stage.description || stage.Description;
                        const stageCode = stage.stage || stage.Stage || stageId;
                        
                        return (
                          <MenuItem key={stageId} value={stageId}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={`${stageCode}`}
                                color={getStageColor(stageId)}
                                size="small"
                              />
                              <Typography>{stageName}</Typography>
                            </Box>
                          </MenuItem>
                        );
                      }) : (
                        <MenuItem disabled>No stages available</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  {stagesError && (
                    <Box sx={{ mt: 1 }}>
                      <Button 
                        size="small" 
                        onClick={loadStages}
                        startIcon={stagesLoading ? <CircularProgress size={16} /> : null}
                        disabled={stagesLoading}
                      >
                        Retry Loading Stages
                      </Button>
                    </Box>
                  )}
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={handleStageUpdate}
                    disabled={stageUpdateLoading || !selectedStage}
                    startIcon={stageUpdateLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                    sx={{ height: 56 }}
                  >
                    {stageUpdateLoading ? 'Updating...' : 'Update Stage'}
                  </Button>
                </Grid>
              </Grid>

              {selectedStage && selectedStage !== applicationData.currentStage?.toString() && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>Stage Change Preview:</strong><br />
                  From: Stage {applicationData.currentStage} - {applicationData.currentStageName}<br />
                  To: Stage {selectedStage} - {stages.find(s => {
                    const stageId = s.stage || s.stageId || s.Id;
                    return stageId == selectedStage;
                  })?.name || stages.find(s => {
                    const stageId = s.id || s.stageId || s.Id;
                    return stageId == selectedStage;
                  })?.stageName || stages.find(s => {
                    const stageId = s.id || s.stageId || s.Id;
                    return stageId == selectedStage;
                  })?.description || 'Unknown Stage'}
                </Alert>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default StageUpdate;